import { selMessage } from "@selector/shared";
import { NotificationIntent, type NotificationProps } from "widgets";

import { DISCONNECTION_QUEUE_CONTAINER_ID } from "@/settings/settings.ts";

import { notify } from "../../modules/notifications/notify.js";
import { CONNECTED_WEBSOCKET, DISCONNECTED_WEBSOCKET, RECONNECTED_WEBSOCKET } from "../action-types.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";

// constants
const WEBSOCKETS_ALERT_DELAY_MILLISECONDS = 3000;

const NOTIFICATION_DISCONNECTED_ID = "websocket_disconnected";

/**
 * State definition
 */
export interface IWebsocketState {
  connected: boolean;
  disconnectionTimestamp: Date;
}

const initialState: IWebsocketState = {
  connected: false,
  disconnectionTimestamp: null,
};

/**
 * Reducers
 */

export default createReducer(initialState, {
  [CONNECTED_WEBSOCKET]: (state: IWebsocketState): IWebsocketState => {
    return {
      ...state,
      connected: true,
    };
  },
  [RECONNECTED_WEBSOCKET]: (state: IWebsocketState): IWebsocketState => {
    return {
      ...state,
      connected: true,
      disconnectionTimestamp: null,
    };
  },
  [DISCONNECTED_WEBSOCKET]: (state: IWebsocketState): IWebsocketState => {
    return {
      ...state,
      connected: false,
      disconnectionTimestamp: new Date(),
    };
  },
});

/**
 * Action creators
 */

export function reconnectedWebsocket(): ReduxAction<void> {
  return (dispatch, getState) => {
    const disconnectionTimestamp = getState().websocket.disconnectionTimestamp;
    const connected = getState().websocket.connected;

    // show an alert only if the connection was disconnected before, and only if this was disconnected more than X seconds
    if (!connected) {
      dispatch({
        type: RECONNECTED_WEBSOCKET,
      });

      // show the "back to normal" message only if we showed the "connection lost" message
      if (
        !!disconnectionTimestamp &&
        new Date().getTime() - disconnectionTimestamp.getTime() > WEBSOCKETS_ALERT_DELAY_MILLISECONDS
      ) {
        //show notification for reconnect success
        const notificationProps = {
          title: selMessage({ id: "websocket.reconnect.successTitle" }),
          children: selMessage({ id: "websocket.reconnect.successMessage" }),
        } satisfies NotificationProps;

        import.meta.env.DEV ||
          (import.meta.env.MODE === "staging" && console.warn("reconnectedWebsocket from websocket.ts"));

        notify.update(NOTIFICATION_DISCONNECTED_ID, notificationProps, NotificationIntent.Info, {
          // overwrite default pause on focus loss to enforce the notification to be unmounted
          pauseOnFocusLoss: false,
          // reset autoclose to 2 seconds to make the notification disappear
          autoClose: 2000,
          containerId: DISCONNECTION_QUEUE_CONTAINER_ID,
        });
      }
    }
  };
}

export function disconnectedWebsocket(): ReduxAction<void> {
  return (dispatch, getState) => {
    if (getState().websocket.connected) {
      dispatch({
        type: DISCONNECTED_WEBSOCKET, // will set connected to false
      });
      setTimeout(() => showDelayedErrorMessage(dispatch, getState), WEBSOCKETS_ALERT_DELAY_MILLISECONDS);
    }
  };
}

function showDelayedErrorMessage(dispatch, getState) {
  // if after the delay the websocket is still disconnected we show the message
  if (!getState().websocket.connected) {
    notify.clearWaitingQueue({ containerId: DISCONNECTION_QUEUE_CONTAINER_ID });
    notify.error(
      {
        title: selMessage({ id: "websocket.delayed.errorTitle" }),
        children: selMessage({ id: "websocket.delayed.errorMessage" }),
      },
      {
        // let the notification remain on screen until reconnection is established
        autoClose: false,
        toastId: NOTIFICATION_DISCONNECTED_ID,
        containerId: DISCONNECTION_QUEUE_CONTAINER_ID,
      },
    );
  }
}

export function connectedWebsocket(): ReduxAction<void> {
  return (dispatch) => {
    dispatch({
      type: CONNECTED_WEBSOCKET,
    });
  };
}
